import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm, type Control } from 'react-hook-form';
import {
  fetchSkill,
  updateSkill,
  uploadSkillFiles,
  uploadSkillMedia,
  fetchSkillFiles,
  fetchSkillFileContent,
  saveSkillFileContent,
  deleteSkillFile,
  fetchUsers,
  syncSkillMetadata,
  fetchSkills,
  reviewSkill,
  type Skill,
  type SkillUpdate,
  type SkillFileInfo,
  type User,
} from '@/admin/lib/api';
import { Badge } from '@/admin/components/ui/badge';
import { Button } from '@/admin/components/ui/button';
import { Skeleton } from '@/admin/components/ui/skeleton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/admin/components/ui/alert-dialog';
import { Textarea } from '@/admin/components/ui/textarea';
import {
  IconArrowLeft,
  IconCheck,
  IconFile,
  IconUser,
  IconX,
} from '@tabler/icons-react';

import { BasicInfoSection } from '@/admin/components/skill/basic-info-section';
import { BlogSection } from '@/admin/components/skill/blog-section';
import { SeoSection } from '@/admin/components/skill/seo-section';
import { FilesSection } from '@/admin/components/skill/files-section';
import { SettingsSidebar } from '@/admin/components/skill/settings-sidebar';
import { useUnsavedChanges } from '@/admin/hooks/use-unsaved-changes';

// Form data type for react-hook-form
export interface SkillFormData {
  // Basic Info
  display_name: string;
  version: string;
  category: string;
  author_id: string;
  featured: boolean;
  status: string;

  // Markdown content for skill page
  markdown_content: string;

  // SEO
  seo_title: string;
  seo_description: string;
  seo_image: string;
  seo_keywords: string;
  canonical_url: string;
}

// Export Control type for child components
export type SkillFormControl = Control<SkillFormData>;

export const SkillDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [skill, setSkill] = useState<Skill | null>(null);
  const [uploadingHero, setUploadingHero] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // File management states
  const [activeDir, setActiveDir] = useState<'skill' | 'assets'>('skill');
  const [files, setFiles] = useState<SkillFileInfo[]>([]);
  const [assets, setAssets] = useState<SkillFileInfo[]>([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [selectedFile, setSelectedFile] = useState<string | null>(null);
  const [fileContent, setFileContent] = useState<string | null>(null);
  const [editedContent, setEditedContent] = useState<string | null>(null);
  const [fileContentType, setFileContentType] = useState<string | null>(null);
  const [fileBlobUrl, setFileBlobUrl] = useState<string | null>(null);
  const [loadingContent, setLoadingContent] = useState(false);
  const [savingFile, setSavingFile] = useState(false);

  // User list for author selection
  const [users, setUsers] = useState<User[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Delete file confirmation dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [pendingDeletePath, setPendingDeletePath] = useState<string | null>(
    null
  );
  const [pendingDeleteIsFolder, setPendingDeleteIsFolder] = useState(false);

  // Blog media upload state
  const [uploadingBlogMedia, setUploadingBlogMedia] = useState(false);

  // Saving states for individual sections
  const [savingBlog, setSavingBlog] = useState(false);
  const [savingSeo, setSavingSeo] = useState(false);

  // Review state (for community skills)
  const [reviewingAction, setReviewingAction] = useState<string | null>(null);
  const [rejectNote, setRejectNote] = useState('');
  const [showRejectForm, setShowRejectForm] = useState(false);

  // React Hook Form
  const form = useForm<SkillFormData>({
    defaultValues: {
      display_name: '',
      version: '',
      category: '',
      author_id: '',
      featured: false,
      status: 'draft',
      markdown_content: '',
      seo_title: '',
      seo_description: '',
      seo_image: '',
      seo_keywords: '',
      canonical_url: '',
    },
  });

  const { control, reset, formState, getValues, setValue, watch } = form;
  const { isDirty } = formState;

  // File content dirty check (separate from form)
  const hasFileUnsavedChanges =
    editedContent !== null &&
    fileContent !== null &&
    editedContent !== fileContent;

  // Combined dirty check
  const hasUnsavedChanges = isDirty || hasFileUnsavedChanges;

  // Unsaved changes warning
  const { isBlocked, confirmNavigation, cancelNavigation } =
    useUnsavedChanges(hasUnsavedChanges);

  const loadUsers = useCallback(async () => {
    setLoadingUsers(true);
    try {
      const result = await fetchUsers(100, 0);
      setUsers(result.users || []);
    } catch (err) {
      console.error('Failed to load users:', err);
    } finally {
      setLoadingUsers(false);
    }
  }, []);

  const loadFiles = useCallback(
    async (dir: 'skill' | 'assets' = 'skill') => {
      if (!id) return;
      setLoadingFiles(true);
      try {
        const result = await fetchSkillFiles(id, dir);
        if (dir === 'skill') {
          setFiles(result.files || []);
        } else {
          setAssets(result.files || []);
        }
      } catch (err) {
        console.error('Failed to load files:', err);
      } finally {
        setLoadingFiles(false);
      }
    },
    [id]
  );

  const loadAllFiles = useCallback(async () => {
    if (!id) return;
    await Promise.all([loadFiles('skill'), loadFiles('assets')]);
  }, [id, loadFiles]);

  const handleViewFile = async (filePath: string) => {
    if (!id) return;
    setSelectedFile(filePath);
    setLoadingContent(true);
    setFileContent(null);
    setEditedContent(null);
    setFileContentType(null);
    if (fileBlobUrl) {
      URL.revokeObjectURL(fileBlobUrl);
      setFileBlobUrl(null);
    }
    try {
      const result = await fetchSkillFileContent(id, filePath, activeDir);
      setFileContent(result.content);
      setEditedContent(result.content);
      setFileContentType(result.contentType);
      if (result.blobUrl) {
        setFileBlobUrl(result.blobUrl);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load file');
      setSelectedFile(null);
    } finally {
      setLoadingContent(false);
    }
  };

  const [loadingSkills, setLoadingSkills] = useState(false);
  const [skills, setSkills] = useState<Skill[]>([]);

  const loadSkill = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      const data = await fetchSkill(id);
      setSkill(data);

      // Reset form with fetched data
      reset({
        display_name: data.display_name || '',
        version: data.version || '',
        category: data.category || '',
        author_id: data.author_id || '',
        featured: data.featured || false,
        status: data.status || 'draft',
        markdown_content: data.markdown_content || '',
        seo_title: data.seo_title || '',
        seo_description: data.seo_description || '',
        seo_image: data.seo_image || '',
        seo_keywords: data.seo_keywords?.join(', ') || '',
        canonical_url: data.canonical_url || '',
      });

      loadAllFiles();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load skill');
    } finally {
      setLoading(false);
    }
  }, [id, loadAllFiles, reset]);

  const loadSkills = useCallback(async () => {
    setLoadingSkills(true);
    try {
      const result = await fetchSkills(100, 0); // Fetch available skills for picker
      setSkills(result.skills || []);
    } catch (err) {
      console.error('Failed to load skills:', err);
    } finally {
      setLoadingSkills(false);
    }
  }, []);

  useEffect(() => {
    if (!id) return;
    loadSkill();
    loadUsers();
    loadSkills();
  }, [id, loadSkill, loadUsers, loadSkills]);

  useEffect(() => {
    return () => {
      if (fileBlobUrl) {
        URL.revokeObjectURL(fileBlobUrl);
      }
    };
  }, [fileBlobUrl]);

  const handleDeleteFile = (path: string) => {
    if (!id) return;
    const currentFiles = activeDir === 'skill' ? files : assets;
    const isFolder = currentFiles.some(f => f.path.startsWith(path + '/'));
    setPendingDeletePath(path);
    setPendingDeleteIsFolder(isFolder);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteFile = async () => {
    if (!id || !pendingDeletePath) return;

    const path = pendingDeletePath;
    const isFolder = pendingDeleteIsFolder;
    const currentFiles = activeDir === 'skill' ? files : assets;

    setDeleteDialogOpen(false);
    setPendingDeletePath(null);

    try {
      if (isFolder) {
        const filesToDelete = currentFiles.filter(
          f => f.path.startsWith(path + '/') || f.path === path
        );
        await Promise.all(
          filesToDelete.map(file => deleteSkillFile(id, file.path, activeDir))
        );
        setSuccess(`Deleted ${filesToDelete.length} files in ${path}`);
      } else {
        await deleteSkillFile(id, path, activeDir);
        setSuccess(`Deleted ${path}`);
      }
      setTimeout(() => setSuccess(null), 3000);
      loadFiles(activeDir);

      if (
        selectedFile === path ||
        (selectedFile && selectedFile.startsWith(path + '/'))
      ) {
        setSelectedFile(null);
        setFileContent(null);
      }
    } catch (err) {
      console.error(err);
      setError(err instanceof Error ? err.message : 'Failed to delete');
    }
  };

  const closePreview = () => {
    setSelectedFile(null);
    setFileContent(null);
    setEditedContent(null);
    setFileContentType(null);
    if (fileBlobUrl) {
      URL.revokeObjectURL(fileBlobUrl);
      setFileBlobUrl(null);
    }
  };

  const handleSaveFile = async () => {
    if (!id || !selectedFile || editedContent === null) return;
    setSavingFile(true);
    setError(null);
    try {
      const result = await saveSkillFileContent(
        id,
        selectedFile,
        editedContent,
        activeDir
      );
      setFileContent(editedContent);
      setSuccess('File saved');
      setTimeout(() => setSuccess(null), 2000);

      if (result.metadata_synced) {
        const updated = await fetchSkill(id);
        setSkill(updated);
      }
      if (result.sync_error) {
        setError(`Metadata sync failed: ${result.sync_error}`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save file');
    } finally {
      setSavingFile(false);
    }
  };

  const handleSaveAll = async () => {
    if (!id) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const values = getValues();
      const updateData: SkillUpdate = {
        display_name: values.display_name || undefined,
        version: values.version || undefined,
        category: values.category || undefined,
        author_id: values.author_id || undefined,
        featured: values.featured,
        status: values.status,
        markdown_content: values.markdown_content || undefined,
        seo_title: values.seo_title || undefined,
        seo_description: values.seo_description || undefined,
        seo_image: values.seo_image || undefined,
        seo_keywords: values.seo_keywords
          ? values.seo_keywords
              .split(',')
              .map(k => k.trim())
              .filter(Boolean)
          : undefined,
        canonical_url: values.canonical_url || undefined,
      };

      const updated = await updateSkill(id, updateData);
      setSkill(updated);

      // Reset form to mark as clean
      reset(values);

      setSuccess('Saved');
      setTimeout(() => setSuccess(null), 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleSyncMetadata = async () => {
    if (!id) return;
    setSyncing(true);
    setError(null);
    try {
      const updated = await syncSkillMetadata(id);
      setSkill(updated);
      // Update only the synced fields without resetting entire form
      setValue('display_name', updated.display_name || '', {
        shouldDirty: false,
      });
      setSuccess('Synced from SKILL.md');
      setTimeout(() => setSuccess(null), 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to sync metadata');
    } finally {
      setSyncing(false);
    }
  };

  const handleReviewApprove = async () => {
    if (!id) return;
    setReviewingAction('approve');
    setError(null);
    try {
      const updated = await reviewSkill(id, { status: 'approved' });
      setSkill(updated);
      setSuccess('Skill approved and published');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to approve skill');
    } finally {
      setReviewingAction(null);
    }
  };

  const handleReviewReject = async () => {
    if (!id) return;
    setReviewingAction('reject');
    setError(null);
    try {
      const updated = await reviewSkill(id, {
        status: 'rejected',
        note: rejectNote || undefined,
      });
      setSkill(updated);
      setShowRejectForm(false);
      setRejectNote('');
      setSuccess('Skill rejected');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reject skill');
    } finally {
      setReviewingAction(null);
    }
  };

  const uploadFiles = async (filesToUpload: File[]) => {
    if (!id || filesToUpload.length === 0) return;

    setUploading(true);
    setError(null);
    try {
      const result = await uploadSkillFiles(id, filesToUpload, activeDir);

      if (result.validation_errors?.length) {
        setError(result.validation_errors.join('; '));
      } else {
        setSuccess(
          `Uploaded ${result.files_count} file${result.files_count > 1 ? 's' : ''}`
        );
        setTimeout(() => setSuccess(null), 3000);
      }

      loadFiles(activeDir);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload files');
    } finally {
      setUploading(false);
    }
  };

  const handleHeroUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!id || !e.target.files?.length) return;
    const file = e.target.files[0];

    setUploadingHero(true);
    setError(null);
    try {
      const result = await uploadSkillMedia(id, file, 'hero');
      setSkill(prev =>
        prev ? { ...prev, hero_url: result.url, hero_path: result.path } : null
      );
      loadFiles('assets');
      setSuccess('Hero image updated');
      setTimeout(() => setSuccess(null), 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload hero');
    } finally {
      setUploadingHero(false);
    }
  };

  const handleVideoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!id || !e.target.files?.length) return;
    const file = e.target.files[0];

    setUploadingVideo(true);
    setError(null);
    try {
      const result = await uploadSkillMedia(id, file, 'video');
      setSkill(prev =>
        prev
          ? {
              ...prev,
              video_url: result.url,
              video_path: result.path,
            }
          : null
      );
      loadFiles('assets');
      setSuccess('Cover video updated');
      setTimeout(() => setSuccess(null), 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload video');
    } finally {
      setUploadingVideo(false);
    }
  };

  const handleBlogMediaUpload = useCallback(
    async (filesToUpload: File[]): Promise<{ path: string; url: string }[]> => {
      if (!id || filesToUpload.length === 0) return [];

      setUploadingBlogMedia(true);
      setError(null);

      const uploadedAssets: { path: string; url: string }[] = [];

      try {
        const validFiles = filesToUpload.filter(
          f => f.type.startsWith('image/') || f.type.startsWith('video/')
        );

        if (validFiles.length === 0) return [];

        const result = await uploadSkillFiles(id, validFiles, 'assets');

        if (result.uploaded_file_urls) {
          uploadedAssets.push(...result.uploaded_file_urls);
          setSuccess(
            `Uploaded ${result.uploaded_file_urls.length} media file(s)`
          );
          setTimeout(() => setSuccess(null), 2000);
          loadFiles('assets');
          // Refresh skill to get updated assets mapping
          const updatedSkill = await fetchSkill(id);
          setSkill(updatedSkill);
        } else if (result.validation_errors?.length) {
          setError(result.validation_errors.join('; '));
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to upload media');
      } finally {
        setUploadingBlogMedia(false);
      }
      return uploadedAssets;
    },
    [id, loadFiles]
  );

  const handleSaveBlog = async () => {
    if (!id) return;
    setSavingBlog(true);
    try {
      const markdownContent = getValues('markdown_content');
      await updateSkill(id, { markdown_content: markdownContent });
      // Reset this field to mark as clean
      reset({ ...getValues() }, { keepValues: true });
      setSuccess('Blog content saved');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to save blog content'
      );
    } finally {
      setSavingBlog(false);
    }
  };

  const handleSaveSeo = async () => {
    if (!id) return;
    setSavingSeo(true);
    try {
      const values = getValues();
      await updateSkill(id, {
        seo_title: values.seo_title || undefined,
        seo_description: values.seo_description || undefined,
        seo_image: values.seo_image || undefined,
        seo_keywords: values.seo_keywords
          ? values.seo_keywords
              .split(',')
              .map(k => k.trim())
              .filter(Boolean)
          : undefined,
        canonical_url: values.canonical_url || undefined,
      });
      // Reset to mark as clean
      reset({ ...getValues() }, { keepValues: true });
      setSuccess('SEO metadata saved');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to save SEO metadata'
      );
    } finally {
      setSavingSeo(false);
    }
  };

  // Handle status change from sidebar
  const handleStatusChange = async (status: string) => {
    if (!id) return;
    try {
      setValue('status', status);
      await updateSkill(id, { status });
      setSkill(prev => (prev ? { ...prev, status } : null));
      setSuccess('Status updated');
      setTimeout(() => setSuccess(null), 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update status');
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'published':
        return (
          <Badge className="bg-green-500 hover:bg-green-600">Published</Badge>
        );
      case 'archived':
        return <Badge variant="secondary">Archived</Badge>;
      default:
        return <Badge variant="outline">Draft</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col h-full bg-background">
        <div className="flex items-center justify-between px-6 py-3 border-b bg-card">
          <div className="flex items-center gap-4">
            <Skeleton className="h-8 w-20" />
            <Skeleton className="h-5 w-32" />
          </div>
          <Skeleton className="h-9 w-28" />
        </div>
        <div className="flex flex-1 overflow-hidden">
          <div className="flex-1 p-6">
            <Skeleton className="h-10 w-80 mb-4" />
            <Skeleton className="h-[400px] w-full" />
          </div>
          <div className="w-64 border-l p-6 space-y-4">
            <Skeleton className="h-6 w-32" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-32 w-full" />
          </div>
        </div>
      </div>
    );
  }

  if (!skill) {
    return (
      <div className="flex flex-col h-full items-center justify-center bg-background">
        <div className="text-center p-8">
          <div className="w-16 h-16 rounded-full bg-muted mx-auto mb-4 flex items-center justify-center">
            <IconFile className="h-8 w-8 text-muted-foreground" />
          </div>
          <h2 className="text-lg font-medium mb-1">Skill not found</h2>
          <p className="text-sm text-muted-foreground mb-4">
            The skill you're looking for doesn't exist.
          </p>
          <Button variant="outline" onClick={() => navigate('/skills')}>
            <IconArrowLeft className="h-4 w-4 mr-2" />
            Back to Skills
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Top Navigation */}
      <div className="flex items-center justify-between px-6 py-3 border-b bg-card shrink-0">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            className="-ml-2 h-8 text-muted-foreground hover:text-foreground"
            onClick={() => navigate('/skills')}
          >
            <IconArrowLeft className="h-4 w-4 mr-1" />
            Skills
          </Button>
          <span className="text-muted-foreground/50">/</span>
          <div className="flex items-center gap-2">
            <h1 className="text-base font-medium">
              {skill.display_name || skill.name}
            </h1>
            {getStatusBadge(skill.status)}
            {isDirty && (
              <span className="text-xs text-amber-600 dark:text-amber-400">
                (unsaved)
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          {success && (
            <span className="text-xs text-emerald-600 dark:text-emerald-400 flex items-center gap-1 animate-in fade-in slide-in-from-right-2 bg-emerald-500/10 px-2 py-1 rounded">
              <IconCheck className="h-3 w-3" /> {success}
            </span>
          )}
          {error && (
            <button
              onClick={() => setError(null)}
              className="text-xs text-destructive flex items-center gap-1 bg-destructive/10 px-2 py-1 rounded hover:bg-destructive/20 transition-colors max-w-md"
              title="Click to dismiss"
              aria-label="Dismiss error"
            >
              <IconX className="h-3 w-3 shrink-0" />
              <span className="line-clamp-2 text-left">{error}</span>
            </button>
          )}
          <Button
            size="sm"
            onClick={handleSaveAll}
            disabled={saving || !isDirty}
          >
            {saving ? 'Saving...' : 'Save All'}
          </Button>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden min-h-0">
        {/* Main Content Area - Scrollable Sections */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-4">
            {/* Community Skill Review Banner */}
            {skill.source === 'community' && (
              <div className="border rounded-lg bg-card p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <IconUser className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">
                      Community Submission
                    </span>
                    {skill.review_status === 'pending_review' && (
                      <Badge className="bg-amber-500/15 text-amber-600 dark:text-amber-400 border-0 font-medium">
                        Pending Review
                      </Badge>
                    )}
                    {skill.review_status === 'approved' && (
                      <Badge className="bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-0 font-medium">
                        Approved
                      </Badge>
                    )}
                    {skill.review_status === 'rejected' && (
                      <Badge className="bg-destructive/15 text-destructive border-0 font-medium">
                        Rejected
                      </Badge>
                    )}
                  </div>
                  {skill.review_status === 'pending_review' && (
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-emerald-600 border-emerald-200 hover:bg-emerald-500/10"
                        disabled={reviewingAction !== null}
                        onClick={handleReviewApprove}
                      >
                        <IconCheck className="h-4 w-4 mr-1" />
                        {reviewingAction === 'approve'
                          ? 'Approving...'
                          : 'Approve'}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-destructive border-destructive/20 hover:bg-destructive/10"
                        disabled={reviewingAction !== null}
                        onClick={() => setShowRejectForm(!showRejectForm)}
                      >
                        <IconX className="h-4 w-4 mr-1" />
                        Reject
                      </Button>
                    </div>
                  )}
                </div>
                <div className="text-sm text-muted-foreground space-y-1">
                  {skill.submitter && (
                    <p>
                      Submitted by{' '}
                      <span className="font-medium text-foreground">
                        {skill.submitter.display_name || skill.submitter.email}
                      </span>
                      {skill.submitted_at && (
                        <>
                          {' '}
                          on {new Date(skill.submitted_at).toLocaleDateString()}
                        </>
                      )}
                    </p>
                  )}
                  {skill.review_status === 'rejected' && skill.review_note && (
                    <p className="text-destructive">
                      Rejection reason: {skill.review_note}
                    </p>
                  )}
                </div>
                {showRejectForm && skill.review_status === 'pending_review' && (
                  <div className="space-y-2 pt-1">
                    <Textarea
                      value={rejectNote}
                      onChange={e => setRejectNote(e.target.value)}
                      placeholder="Reason for rejection (optional)..."
                      rows={2}
                    />
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="destructive"
                        disabled={reviewingAction !== null}
                        onClick={handleReviewReject}
                      >
                        {reviewingAction === 'reject'
                          ? 'Rejecting...'
                          : 'Confirm Reject'}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setShowRejectForm(false);
                          setRejectNote('');
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Basic Info Section */}
            <BasicInfoSection
              control={control}
              users={users}
              loadingUsers={loadingUsers}
              syncing={syncing}
              onSync={handleSyncMetadata}
              skillName={skill.name}
              skillDescription={skill.description}
            />

            {/* Blog Content Section */}
            <BlogSection
              control={control}
              onSaveContent={handleSaveBlog}
              savingContent={savingBlog}
              onUploadMedia={handleBlogMediaUpload}
              uploadingMedia={uploadingBlogMedia}
              skills={skills}
              loadingSkills={loadingSkills}
              assetsMapping={skill.assets}
            />

            {/* SEO Section */}
            <SeoSection
              control={control}
              onSaveSeo={handleSaveSeo}
              savingSeo={savingSeo}
              defaultTitle={skill.display_name || skill.name}
              defaultDescription={skill.description || ''}
              defaultImage={skill.hero_url || ''}
              itemName={skill.name}
            />

            {/* Files Section */}
            <FilesSection
              activeDir={activeDir}
              onActiveDirChange={setActiveDir}
              files={files}
              assets={assets}
              loadingFiles={loadingFiles}
              onRefreshFiles={() => loadFiles(activeDir)}
              onUpload={uploadFiles}
              uploading={uploading}
              selectedFile={selectedFile}
              onSelectFile={handleViewFile}
              fileContent={fileContent}
              fileContentType={fileContentType}
              fileBlobUrl={fileBlobUrl}
              loadingContent={loadingContent}
              onSaveFile={handleSaveFile}
              savingFile={savingFile}
              hasUnsavedChanges={hasFileUnsavedChanges}
              onClosePreview={closePreview}
              editedContent={editedContent}
              onContentChange={setEditedContent}
              onDeleteFile={handleDeleteFile}
            />
          </div>
        </div>

        {/* Right Sidebar */}
        <SettingsSidebar
          skill={skill}
          control={control}
          watch={watch}
          onStatusChange={handleStatusChange}
          uploadingHero={uploadingHero}
          onHeroUpload={handleHeroUpload}
          uploadingVideo={uploadingVideo}
          onVideoUpload={handleVideoUpload}
          syncing={syncing}
          onSync={handleSyncMetadata}
        />
      </div>

      {/* Delete file confirmation dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pendingDeleteIsFolder ? 'Delete Folder' : 'Delete File'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pendingDeleteIsFolder
                ? `Are you sure you want to delete folder "${pendingDeletePath}" and all its contents? This action cannot be undone.`
                : `Are you sure you want to delete "${pendingDeletePath}"? This action cannot be undone.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteFile}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Navigation blocked dialog */}
      <AlertDialog open={isBlocked} onOpenChange={() => cancelNavigation()}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unsaved Changes</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. Are you sure you want to leave? Your
              changes will be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={cancelNavigation}>
              Stay
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmNavigation}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Leave
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
